import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import pLimit from "p-limit";
import ApiData from "../../../api/ApiData";
import { getCalendarYearRange } from "../../../utils/productionAuditPeriodUtils";
import {
  auditCompanyProduction,
  buildCompanyNameMap,
} from "../../../utils/productionAuditRunner";
import { BtnPrimaryv4 } from "../../UI/button/BtnPrimaryv4";
import { BtnOutlineV4 } from "../../UI/button/BtnOutlineV4";
import { ProductionCatalogLoader } from "./ProductionCatalogLoader";

const BATCH_CONCURRENCY = 3;

function AnomalyDetail({ result }) {
  if (!result) {
    return null;
  }

  return (
    <div className="production-audit-detail">
      <p className="production-audit-muted">
        Период: {result.period?.from} — {result.period?.to}
      </p>
      {result.extraInReport.length > 0 ? (
        <section className="production-audit-anomaly-block">
          <h3>
            Лишние в отчёте{" "}
            <span className="production-audit-badge production-audit-badge--warn">
              {result.counts.extraInReport}
            </span>
          </h3>
          <ul className="production-audit-anomaly-list">
            {result.extraInReport.map((row) => (
              <li key={row.productionId}>
                <strong>ID {row.productionId}</strong> — {row.reportName} /{" "}
                {row.reportAddress}; печать: {row.all_label_prod}. Владелец в
                справочнике: {row.ownerCompanyName} (company_id{" "}
                {row.ownerCompanyId ?? "—"})
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {result.metadataMismatch.length > 0 ? (
        <section className="production-audit-anomaly-block">
          <h3>
            Расхождение названия/адреса{" "}
            <span className="production-audit-badge production-audit-badge--warn">
              {result.counts.metadataMismatch}
            </span>
          </h3>
          <ul className="production-audit-anomaly-list">
            {result.metadataMismatch.map((row) => (
              <li key={row.productionId}>
                <strong>ID {row.productionId}</strong> — ЛК: {row.lkName} /{" "}
                {row.lkAddress}; отчёт: {row.reportName} / {row.reportAddress}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {result.onlyInLk.length > 0 ? (
        <section className="production-audit-anomaly-block">
          <h3>
            Только в ЛК (возможно нет PRINT){" "}
            <span className="production-audit-badge">{result.counts.onlyInLk}</span>
          </h3>
          <ul className="production-audit-anomaly-list production-audit-anomaly-list--compact">
            {result.onlyInLk.map((row) => (
              <li key={row.productionId}>
                ID {row.productionId}: {row.lkName}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {!result.hasAnomalies ? (
        <p>Критичных расхождений (лишние в отчёте / метаданные) не найдено.</p>
      ) : null}
    </div>
  );
}

export function ProductionReportMismatchTab({
  catalogRows,
  onCatalogLoaded,
  companyNames,
  setCompanyNames,
}) {
  const year = getCalendarYearRange().year;
  const periodLabel = useMemo(() => {
    const { from, to } = getCalendarYearRange(year);
    return `${from} — ${to}`;
  }, [year]);

  const [companyIdInput, setCompanyIdInput] = useState("5");
  const [singleResult, setSingleResult] = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);
  const [batchRows, setBatchRows] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await ApiData.fetchCompanyList({ size: ApiData.sizeItem });
      if (!cancelled && Array.isArray(list)) {
        setCompanyNames(buildCompanyNameMap(list));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setCompanyNames]);

  const runSingle = useCallback(async () => {
    const companyId = Number(companyIdInput);
    if (!Number.isFinite(companyId)) {
      return;
    }
    if (!catalogRows?.length) {
      return;
    }
    setSingleLoading(true);
    setSingleResult(null);
    const controller = new AbortController();
    try {
      const result = await auditCompanyProduction({
        companyId,
        catalogRows,
        companyNames,
        year,
        signal: controller.signal,
      });
      setSingleResult(result);
    } catch {
      setSingleResult(null);
    } finally {
      setSingleLoading(false);
    }
  }, [catalogRows, companyIdInput, companyNames, year]);

  const cancelBatch = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setBatchLoading(false);
  }, []);

  const runBatch = useCallback(async () => {
    if (!catalogRows?.length) {
      return;
    }
    const list = await ApiData.fetchCompanyList({ size: ApiData.sizeItem });
    const companies = Array.isArray(list) ? list : [];
    const names = buildCompanyNameMap(companies);
    setCompanyNames(names);

    const controller = new AbortController();
    abortRef.current = controller;
    setBatchLoading(true);
    setBatchRows([]);
    setBatchProgress({ done: 0, total: companies.length });

    const limit = pLimit(BATCH_CONCURRENCY);

    try {
      const tasks = companies.map((company) =>
        limit(async () => {
          if (controller.signal.aborted) {
            return null;
          }
          const companyId = Number(company.id);
          if (!Number.isFinite(companyId)) {
            return null;
          }
          try {
            const audit = await auditCompanyProduction({
              companyId,
              catalogRows,
              companyNames: names,
              year,
              signal: controller.signal,
            });
            return {
              companyId,
              name: names.get(companyId) ?? company.name_company,
              ...audit,
            };
          } catch (e) {
            if (e?.name === "AbortError") {
              return null;
            }
            return {
              companyId,
              name: names.get(companyId) ?? company.name_company,
              error: true,
              hasAnomalies: false,
              counts: {
                extraInReport: 0,
                onlyInLk: 0,
                metadataMismatch: 0,
              },
            };
          } finally {
            setBatchProgress((p) => ({
              ...p,
              done: Math.min(p.done + 1, companies.length),
            }));
          }
        }),
      );
      const settled = (await Promise.all(tasks)).filter(Boolean);
      if (!controller.signal.aborted) {
        const results = [...settled];
        results.sort((a, b) => {
          if (a.hasAnomalies !== b.hasAnomalies) {
            return a.hasAnomalies ? -1 : 1;
          }
          const scoreA =
            (a.counts?.extraInReport ?? 0) + (a.counts?.metadataMismatch ?? 0);
          const scoreB =
            (b.counts?.extraInReport ?? 0) + (b.counts?.metadataMismatch ?? 0);
          return scoreB - scoreA;
        });
        setBatchRows(results);
      }
    } finally {
      setBatchLoading(false);
      abortRef.current = null;
    }
  }, [catalogRows, setCompanyNames, year]);

  const selectedBatch = batchRows.find((r) => r.companyId === selectedBatchId);

  return (
    <section className="card cc-v4-soft-list-root admin-panel-section production-audit-section">
      <ProductionCatalogLoader
        onLoaded={onCatalogLoaded}
        loadedCount={catalogRows?.length ?? 0}
      />

      <p className="production-audit-muted">
        Сравнение с отчётом PRINT за год ({periodLabel})
      </p>

      <div className="production-audit-toolbar">
        <label className="production-audit-field">
          <span>ID компании</span>
          <input
            type="number"
            className="production-audit-input"
            value={companyIdInput}
            onChange={(e) => setCompanyIdInput(e.target.value)}
          />
        </label>
        <BtnPrimaryv4
          type="button"
          text={singleLoading ? "Проверка…" : "Проверить компанию"}
          onClick={runSingle}
          disabled={singleLoading || batchLoading || !catalogRows?.length}
        />
        <BtnPrimaryv4
          type="button"
          text={batchLoading ? "Пакетная проверка…" : "Проверить все компании"}
          onClick={runBatch}
          disabled={singleLoading || batchLoading || !catalogRows?.length}
        />
        {batchLoading ? (
          <BtnOutlineV4 type="button" text="Отмена" onClick={cancelBatch} />
        ) : null}
      </div>

      {batchLoading ? (
        <div className="production-audit-progress">
          <div
            className="production-audit-progress-bar"
            style={{
              width: `${
                batchProgress.total
                  ? Math.round(
                      (batchProgress.done / batchProgress.total) * 100,
                    )
                  : 0
              }%`,
            }}
          />
          <span className="production-audit-progress-label">
            {batchProgress.done} / {batchProgress.total} компаний
          </span>
        </div>
      ) : null}

      {singleResult ? (
        <div className="production-audit-detail-wrap">
          <h3>
            Компания ID {singleResult.companyId}
            {companyNames.get(singleResult.companyId)
              ? ` — ${companyNames.get(singleResult.companyId)}`
              : ""}
          </h3>
          <AnomalyDetail result={singleResult} />
        </div>
      ) : null}

      {batchRows.length > 0 ? (
        <div className="production-audit-batch">
          <h3>Результаты пакетной проверки</h3>
          <div className="cc-v4-soft-grid production-audit-batch-grid">
            <span className="cc-v4-soft-th-static">ID</span>
            <span className="cc-v4-soft-th-static">Компания</span>
            <span className="cc-v4-soft-th-static">Аномалии</span>
            <span className="cc-v4-soft-th-static">Лишние</span>
            <span className="cc-v4-soft-th-static">Метаданные</span>
            {batchRows.map((row) => (
              <React.Fragment key={row.companyId}>
                <button
                  type="button"
                  className="production-audit-batch-link admin-users-cell"
                  onClick={() => setSelectedBatchId(row.companyId)}
                >
                  {row.companyId}
                </button>
                <span className="admin-users-cell">{row.name}</span>
                <span className="admin-users-cell">
                  {row.hasAnomalies ? "Да" : row.error ? "Ошибка" : "Нет"}
                </span>
                <span className="admin-users-cell">
                  {row.counts?.extraInReport ?? 0}
                </span>
                <span className="admin-users-cell">
                  {row.counts?.metadataMismatch ?? 0}
                </span>
              </React.Fragment>
            ))}
          </div>
          {selectedBatch ? (
            <div className="production-audit-detail-wrap">
              <h4>Детали: {selectedBatch.name}</h4>
              <AnomalyDetail result={selectedBatch} />
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
